/**
 * ImpactSection.tsx - Impact Highlights Configuration Section
 *
 * This component provides an interface for configuring impact highlights
 * in the fundraiser builder details step with management and preview capabilities.
 *
 * Key Features:
 * - Impact highlights management interface
 * - Integration with ImpactHighlightsManager component
 * - Preview of configured impact highlights
 * - Add, edit, and delete impact items
 * - Drag-and-drop reordering support
 * - Icon and color customization
 *
 * Impact Configuration:
 * - Icon selection from Lucide icon library
 * - Custom color theming for each impact item
 * - Numerical metrics and achievement values
 * - Descriptive text for context and explanation
 * - Display order management
 *
 * Management Features:
 * - Delegated to ImpactHighlightsManager component
 * - Consistent interface with other builder sections
 * - Real-time preview updates
 * - Validation and error handling
 *
 * Props: None (uses MobX store for state management)
 *
 * Dependencies: ImpactHighlightsManager component, MobX store
 */

import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { observer } from "mobx-react-lite";
import { ImpactManager } from "../ImpactHighlightsManager";
import { CreateFundraiserModel } from "../../../models";
import { fundraiserStore } from "../../../stores";

const Section = styled.div`
  background: #f8fafc;
  border-radius: 16px;
  padding: 32px;
  border: 1px solid #e2e8f0;
`;

interface ImpactSectionProps {
  builderData: CreateFundraiserModel;
  onKeyDown?: (e: React.KeyboardEvent, isLastField: boolean) => void;
}

export const ImpactSection: React.FC<ImpactSectionProps> = observer(
  ({ builderData, onKeyDown }) => {
    const sectionRef = useRef<HTMLDivElement>(null);
    const hasImpactError = fundraiserStore.builderData.errors.some((err) =>
      err.field?.startsWith("impact")
    );

    // Auto-open section and scroll to it when there's a media error
    useEffect(() => {
      if (hasImpactError && fundraiserStore.shouldScrollToImpactSection) {
        // Then scroll to it with a delay to allow the section to expand
        setTimeout(() => {
          if (sectionRef.current) {
            const elementTop = sectionRef.current.offsetTop;
            const headerOffset = 100;
            const targetPosition = elementTop - headerOffset;
            window.scrollTo({
              top: targetPosition,
              behavior: "smooth",
            });
          }
          fundraiserStore.setShouldScrollToImpactSection(false);
        }, 150); // Slightly longer delay to ensure section expansion
      } else {
      }
    }, [hasImpactError, fundraiserStore.shouldScrollToImpactSection]);

    return (
      <Section ref={sectionRef} id="impact-section">
        <ImpactManager onKeyDown={onKeyDown} />
      </Section>
    );
  }
);
